import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { 
  Edit, 
  Trash, 
  Download, 
  CheckCircle, 
  XCircle,
  MoreVertical,
  Maximize2,
  Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';

export interface ActionButtonProps {
  onEdit?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  onActivate?: () => void;
  onDeactivate?: () => void;
  onViewDetails?: () => void;
  editLink?: string;
  isActive?: boolean;
  showStatus?: boolean;
  size?: 'xs' | 'sm' | 'md';
}

/**
 * Premium styled action buttons component that combines all actions into a visually appealing format
 */
const ActionButtons: React.FC<ActionButtonProps> = ({
  onEdit,
  onDelete,
  onDownload,
  onActivate,
  onDeactivate,
  onViewDetails,
  editLink,
  isActive = true,
  showStatus = true,
  size = 'md'
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Update dropdown position when menu opens (use fixed for portal)
  useEffect(() => {
    if (menuOpen && menuButtonRef.current) {
      const rect = menuButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.right - 120, // min-w-[120px] - align right edge with button
      });
    }
  }, [menuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setMenuOpen(false);
    if (menuOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [menuOpen]);

  // Determine icon sizes based on the size prop
  const getIconSize = () => {
    switch (size) {
      case 'xs': return 14;
      case 'sm': return 16;
      case 'md': return 18;
      default: return 16;
    }
  };

  const iconSize = getIconSize();
  
  // Determine button classes based on the size prop
  const getButtonClasses = () => {
    const baseClasses = "rounded-md flex items-center justify-center transition-all duration-200 border border-transparent hover:border-gray-700";
    
    switch (size) {
      case 'xs':
        return `${baseClasses} p-1 text-xs`;
      case 'sm':
        return `${baseClasses} p-1.5 text-sm`;
      case 'md':
        return `${baseClasses} p-2 text-sm`;
      default:
        return `${baseClasses} p-1.5 text-sm`;
    }
  };
  
  const buttonClasses = getButtonClasses();

  const hasMenuActions =
    !!onDownload ||
    !!onDelete ||
    (showStatus && ((!isActive && onActivate) || (isActive && onDeactivate)));

  // Combined dropdown menu with all actions
  return (
    <div className="relative">
      {/* Main action buttons - always visible */}
      <div className="flex space-x-2">
        {/* View Details button */}
        {onViewDetails && (
          <button 
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails();
            }}
            className={`${buttonClasses} text-gray-400 hover:text-indigo-400 hover:bg-indigo-900/20 hover:shadow-sm`}
            title="View Details"
          >
            <Eye size={iconSize} />
          </button>
        )}
        
        {/* Edit button */}
        {onEdit ? (
          <button 
            onClick={onEdit}
            className={`${buttonClasses} text-gray-400 hover:text-blue-400 hover:bg-blue-900/20 hover:shadow-sm`}
          >
            <Edit size={iconSize} />
          </button>
        ) : editLink && (
          <Link to={editLink} className={`${buttonClasses} text-gray-400 hover:text-blue-400 hover:bg-blue-900/20 hover:shadow-sm`}>
            <Edit size={iconSize} />
          </Link>
        )}
        
        {/* Menu toggle button — only when dropdown has actions */}
        {hasMenuActions && (
          <button 
            ref={menuButtonRef}
            onClick={(e) => {
              e.stopPropagation();
              if (!menuOpen && menuButtonRef.current) {
                const rect = menuButtonRef.current.getBoundingClientRect();
                setDropdownPosition({
                  top: rect.bottom + 8,
                  left: rect.right - 120,
                });
              }
              setMenuOpen(!menuOpen);
            }}
            className={`${buttonClasses} text-gray-400 hover:text-gray-300 hover:bg-gray-800/50 hover:shadow-sm ${menuOpen ? 'bg-gray-800/50 border-gray-700' : ''}`}
            title="More actions"
            aria-label="More actions"
          >
            <MoreVertical size={iconSize} />
          </button>
        )}
      </div>

      {/* Dropdown menu - rendered via portal to escape card stacking context */}
      {menuOpen && createPortal(
        <div 
          className="fixed p-2 bg-opacity-95 bg-gray-800 backdrop-blur-sm rounded-lg border border-gray-700 shadow-xl flex flex-col gap-2 min-w-[120px] animate-fade-in z-[9999]"
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Download option */}
          {onDownload && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onDownload();
                setMenuOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-green-400 rounded-md hover:bg-green-900/20 transition-colors"
            >
              <Download size={iconSize} />
              <span>Download</span>
            </button>
          )}
          
          {/* Status toggle options */}
          {showStatus && (
            <>
              {!isActive && onActivate && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onActivate();
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-green-400 rounded-md hover:bg-green-900/20 transition-colors"
                >
                  <CheckCircle size={iconSize} />
                  <span>Activate</span>
                </button>
              )}
              
              {isActive && onDeactivate && (
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeactivate();
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-amber-400 rounded-md hover:bg-amber-900/20 transition-colors"
                >
                  <XCircle size={iconSize} />
                  <span>Deactivate</span>
                </button>
              )}
            </>
          )}
          
          {/* Delete option - always at bottom */}
          {onDelete && (
            <button 
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
                setMenuOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:text-red-400 rounded-md hover:bg-red-900/20 transition-colors"
            >
              <Trash size={iconSize} />
              <span>Delete</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ActionButtons; 